import { type FormEvent, type ReactElement, useState } from 'react';
import { Button, FormField } from '@/shared/ui';
import { useSignIn } from '../model/use-sign-in';

/**
 * 밑줄 입력칸 — 고객 웹 로그인과 같은 밑줄 언어. 포커스하면 밑줄이 검게 진해지고,
 * 브라우저 자동완성의 파란 바탕은 흰 바탕으로 덮는다.
 */
const UNDERLINE_INPUT_CLASS =
  'h-12 rounded-none border-0 border-b border-foreground/15 bg-transparent px-0 text-[15px] focus-visible:border-foreground focus-visible:ring-0 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_var(--background)]';

export interface SignInFormProps {
  readonly onSuccess?: () => void;
}

export const SignInForm = ({ onSuccess }: SignInFormProps): ReactElement => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { fieldErrors, formError, isPending, submit } = useSignIn();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (await submit({ email, password })) {
      onSuccess?.();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
      <FormField
        label="이메일"
        type="email"
        autoComplete="email"
        isRequired
        className={UNDERLINE_INPUT_CLASS}
        value={email}
        error={fieldErrors.email}
        onChange={(event) => setEmail(event.target.value)}
      />

      <FormField
        label="비밀번호"
        type="password"
        autoComplete="current-password"
        isRequired
        className={UNDERLINE_INPUT_CLASS}
        value={password}
        error={fieldErrors.password}
        onChange={(event) => setPassword(event.target.value)}
      />

      {formError ? (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      ) : null}

      <Button
        type="submit"
        isLoading={isPending}
        className="mt-2 h-12 w-full justify-between px-6 text-[15px] font-semibold tracking-[-0.01em]"
      >
        로그인
        <span aria-hidden className="text-lg leading-none">
          →
        </span>
      </Button>
    </form>
  );
};
